import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Link as LinkIcon, Mail, Share2, Check, AlertCircle } from 'lucide-react';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ReferralModal = ({ onClose }) => {
  const [copiedLink, setCopiedLink] = useState(false);
  const [emailForm, setEmailForm] = useState({
    recipientName: '',
    recipientEmail: '',
    senderName: '',
    message: "I found SDORA Wellbeing to be a really supportive place for mental health resources and tools. I wanted to share it with you, just in case you need an extra layer of support right now. Take care."
  });
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  const referralLink = window.location.origin;

  // Focus management: focus the modal on open, trap Tab, restore focus on close
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    closeButtonRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedRef.current?.focus?.();
    };
  }, [onClose]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 3000);
  };

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmailForm(prev => ({ ...prev, [name]: value }));
    if (name === 'recipientEmail') setEmailError('');
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || emailSent) return;

    if (!EMAIL_REGEX.test(emailForm.recipientEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    setIsSubmitting(true);

    // Client-side share flow — no PHI is transmitted or stored
    setTimeout(() => {
      setIsSubmitting(false);
      setEmailSent(true);
      setTimeout(() => {
        setEmailSent(false);
        setEmailForm(prev => ({
          recipientName: '',
          recipientEmail: '',
          senderName: '',
          message: prev.message
        }));
      }, 3000);
    }, 400);
  };

  const handleSocialShare = (platform) => {
    const message = encodeURIComponent("Check out SDORA Wellbeing - a supportive place for mental health resources and tools.");
    const url = encodeURIComponent(referralLink);

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      twitter: `https://twitter.com/intent/tweet?text=${message}&url=${url}`
    };

    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4" onClick={onClose}>
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="circle-of-care-title"
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <h2 id="circle-of-care-title" className="text-2xl font-bold text-gray-900">Extend the Circle of Care</h2>
            <p className="text-sm text-gray-600 mt-1">
              Share SDORA with a friend, family member, or someone you care about. This is not a clinical referral.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#66C2B6] rounded"
            aria-label="Close modal"
            data-testid="circle-of-care-close-btn"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Copy Link Section */}
          <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-lg p-6 border border-teal-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-white rounded-full p-2">
                <LinkIcon className="w-5 h-5" style={{ color: '#66C2B6' }} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Quick Share Link</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">Copy this link to share in messages or chats</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={referralLink}
                readOnly
                className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-700"
                data-testid="circle-of-care-link-input"
              />
              <button
                onClick={handleCopyLink}
                className="px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2"
                style={{
                  backgroundColor: copiedLink ? '#499F94' : '#66C2B6',
                  color: 'white'
                }}
                data-testid="circle-of-care-copy-link-btn"
                aria-live="polite"
              >
                {copiedLink ? (
                  <>
                    <Check className="w-5 h-5" />
                    Copied!
                  </>
                ) : (
                  'Copy Link'
                )}
              </button>
            </div>
          </div>

          {/* Email Form Section */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-teal-50 rounded-full p-2">
                <Mail className="w-5 h-5" style={{ color: '#66C2B6' }} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Send Personal Invitation</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">Share privately via email with a personal message</p>

            {emailSent ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3" data-testid="circle-of-care-success">
                <Check className="w-5 h-5 text-green-600" />
                <span className="text-green-800 font-medium">Email invitation sent successfully!</span>
              </div>
            ) : (
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="coc-recipient-name" className="block text-sm font-medium text-gray-700 mb-2">
                      Recipient Name
                    </label>
                    <input
                      id="coc-recipient-name"
                      type="text"
                      name="recipientName"
                      value={emailForm.recipientName}
                      onChange={handleEmailChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Their name"
                      data-testid="circle-of-care-recipient-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="coc-recipient-email" className="block text-sm font-medium text-gray-700 mb-2">
                      Recipient Email
                    </label>
                    <input
                      id="coc-recipient-email"
                      type="email"
                      name="recipientEmail"
                      value={emailForm.recipientEmail}
                      onChange={handleEmailChange}
                      required
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        emailError ? 'border-red-400' : 'border-gray-300'
                      }`}
                      placeholder="their@email.com"
                      data-testid="circle-of-care-recipient-email"
                      aria-invalid={!!emailError}
                      aria-describedby={emailError ? 'coc-email-error' : undefined}
                    />
                    {emailError && (
                      <p id="coc-email-error" className="mt-1 text-xs text-red-600 flex items-center gap-1" data-testid="circle-of-care-email-error">
                        <AlertCircle className="w-3 h-3" /> {emailError}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label htmlFor="coc-sender-name" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name
                  </label>
                  <input
                    id="coc-sender-name"
                    type="text"
                    name="senderName"
                    value={emailForm.senderName}
                    onChange={handleEmailChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Your name"
                    data-testid="circle-of-care-sender-name"
                  />
                </div>
                <div>
                  <label htmlFor="coc-message" className="block text-sm font-medium text-gray-700 mb-2">
                    Personal Message (Optional - You can customize this)
                  </label>
                  <textarea
                    id="coc-message"
                    name="message"
                    value={emailForm.message}
                    onChange={handleEmailChange}
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    data-testid="circle-of-care-message"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Please do not include sensitive medical or personal information in your message.
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 rounded-lg font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: '#66C2B6',
                    color: 'white'
                  }}
                  data-testid="circle-of-care-send-btn"
                >
                  {isSubmitting ? 'Sending...' : 'Send Invitation'}
                </button>
              </form>
            )}
          </div>

          {/* Social Media Share Section */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-teal-50 rounded-full p-2">
                <Share2 className="w-5 h-5" style={{ color: '#66C2B6' }} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Share on Social Media</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">Spread awareness and support</p>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleSocialShare('facebook')}
                className="flex-1 min-w-[150px] flex items-center justify-center gap-2 px-4 py-3 bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-lg font-medium transition-colors"
                data-testid="circle-of-care-share-facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </button>
              <button
                onClick={() => handleSocialShare('linkedin')}
                className="flex-1 min-w-[150px] flex items-center justify-center gap-2 px-4 py-3 bg-[#0A66C2] hover:bg-[#095196] text-white rounded-lg font-medium transition-colors"
                data-testid="circle-of-care-share-linkedin"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn
              </button>
              <button
                onClick={() => handleSocialShare('twitter')}
                className="flex-1 min-w-[150px] flex items-center justify-center gap-2 px-4 py-3 bg-black hover:bg-gray-800 text-white rounded-lg font-medium transition-colors"
                data-testid="circle-of-care-share-twitter"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                X (Twitter)
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Use React Portal to render modal at body level
  return ReactDOM.createPortal(modalContent, document.body);
};

export default ReferralModal;
